import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { UsersService } from '../users.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { User } from '../user.interface';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { atLeastOneFieldValidator } from 'src/app/utils/common/validators/at-least-one-field';
import { DirectiiService } from '../../directii/directii.service';
import { CompartimenteService } from '../../compartimente/compartimente.service';
import { FunctiiService } from '../../functii/functii.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { completeNameValidator } from 'src/app/shared/validators/complete-name';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';

@Component({
    selector: 'app-add-user',
    templateUrl: './add-user.component.html',
    styleUrls: ['./add-user.component.scss'],
})
export class AddUserComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    userTypes: Generic[] = this.data.userTypes;
    directiiActive: Generic[] = this.data.directiiActive;
    compartimenteActive!: Generic[];
    functiiActive!: Generic[];

    private addDestroy$ = new Subject<void>();

    filteredDirectii: any[] = this.data.directiiActive;
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    filteredFunctii: Generic[] = [];
    functiiControl = new FormControl();

    parameters: { key: string; value: string }[] = [
        { key: '-- Alegeți --', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    defaultParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup(
        {
            name: new FormControl('', [Validators.required, completeNameValidator()]),
            slug: new FormControl('', []),
            username: new FormControl('', [Validators.required]),
            email: new FormControl('', [Validators.required, Validators.email]),
            user_type_id: new FormControl('-1', [Validators.required, notDefaultValidator('-1')]),
            telefon_work: new FormControl('', []),
            dgitl_avantax_user_id: new FormControl('', []),
            notificari: new FormControl('', [Validators.required]),
            id_sesizare_departament: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
            id_sesizare_compartiment: new FormControl(-1, []),
            functie_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        },
        { validators: atLeastOneFieldValidator('email', 'username') }
    );

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<AddUserComponent>,
        public usersService: UsersService,
        public directiiService: DirectiiService,
        public compartimenteService: CompartimenteService,
        public functiiService: FunctiiService,
        private notificationService: NotificationService,
        private authService: AuthService
    ) {
        this.processToken();
    }

    ngOnInit(): void {
        this.loadFunctiiActive();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.usersService.setToken(token);
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.functiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredFunctii = this.functiiActive;
                    return of(this.functiiActive);
                }
                return this.filterFunctii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredFunctii = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('id_sesizare_departament')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['id_sesizare_compartiment'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.compartimenteService.getCompartimenteByDirectieId(directieId)
            : this.compartimenteService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        });
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('id_sesizare_compartiment')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadFunctiiActive() {
        this.isLoading = true;
        this.functiiService
            .getFunctiiActive()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.functiiActive = [...res];
                    this.filteredFunctii = [...res];
                },
                error: (err) => {
                    console.log(err);
                    this.functiiActive = [];
                    this.filteredFunctii = [];
                },
            });
    }

    filterFunctii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.functiiActive.filter(functie =>
            this.normalizeString(functie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onFunctieSelectClosed() {
        const selectedValue = this.functiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredFunctii.some(functie =>
                functie.id === this.form.get('functie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.functiiControl.setValue(null);
                this.filteredFunctii = this.functiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    submitForm() {
        if (this.form.invalid) {
            return;
        }

        this.isLoading = true;

        const {
            name,
            username,
            email,
            user_type_id,
            functie_id,
            dgitl_avantax_user_id,
            notificari,
            telefon_work,
            public_view,
            id_sesizare_compartiment,
            id_sesizare_departament,
            slug,
        } = this.form.value as User;

        const newUser: User = {
            name,
            username,
            email,
            user_type_id,
            functie_id,
            dgitl_avantax_user_id,
            notificari,
            telefon_work: "+4" + telefon_work,
            public_view,
            id_sesizare_compartiment,
            id_sesizare_departament,
            slug,
        };

        this.usersService
            .addUser(newUser)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

}
